import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { memberApi } from "../../lib/api";
import AudioPlayer from "../../components/AudioPlayer";
import { useMemberAuth } from "../../context/MemberAuthContext";
import MemberLayout from "../../components/MemberLayout";
import { toast } from "../../hooks/use-toast";
import { ArrowLeft, Calendar, Clock, MapPin, User, Users, Send, X, CheckCircle2, Share2, Headphones } from "lucide-react";

const todayStr = () => new Date().toISOString().slice(0, 10);

const EventTabs = ({ tab, setTab }) => (
  <div className="inline-flex p-1 rounded-full mb-6" style={{ background: '#ece5d3' }} role="tablist" data-testid="events-tabs">
    {[['upcoming', 'Upcoming Events'], ['past', 'Past Masterclasses']].map(([k, label]) => (
      <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`events-tab-${k}`}
        className={`px-4 py-2 rounded-full text-[13px] transition-colors ${tab === k ? 'bg-[#0f3d2e] text-[#f4efe4]' : 'text-[#0d2a20]/75 hover:text-[#0d2a20]'}`}>
        {label}
      </button>
    ))}
  </div>
);

export const EventsList = () => {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'past' ? 'past' : 'upcoming';
  const setTab = (k) => setParams(k === 'past' ? { tab: 'past' } : {}, { replace: true });
  return (
    <MemberLayout>
      <div className="section-eyebrow text-[#0d2a20]/60 mb-2">Members · Events</div>
      <h1 className="hero-title text-[40px] md:text-[56px] leading-none mb-2">Members-only <span className="font-serif-i text-[#e0684d]">events</span></h1>
      <p className="text-[15px] text-[#0d2a20]/75 mb-6 max-w-[640px]">
        {tab === 'past' ? 'Catch up on past masterclasses — listen to the full recordings anytime.' : 'Read the full details before reserving. Seats are limited.'}
      </p>
      <EventTabs tab={tab} setTab={setTab} />
      {tab === 'past' ? <PastMasterclasses /> : <UpcomingEvents />}
    </MemberLayout>
  );
};

const PastMasterclasses = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  useEffect(() => { memberApi.get('/member/recordings').then(({ data }) => setItems(data)).catch(() => setFailed(true)).finally(() => setLoading(false)); }, []);
  if (failed) return <div className="rounded-lg p-10 text-center text-[14px] text-[#7a1f13]" style={{ background: '#fbf8ef' }} data-testid="past-error">We couldn't load recordings right now. Please refresh to try again.</div>;
  if (loading) return <div className="rounded-lg p-10 text-center text-[14px] text-[#0d2a20]/60" style={{ background: '#fbf8ef' }}>Loading…</div>;
  if (items.length === 0) return <div className="rounded-lg p-10 text-center text-[14px] text-[#0d2a20]/60" style={{ background: '#fbf8ef' }} data-testid="past-empty">No recordings available yet. Check back after our next masterclass.</div>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="past-list">
      {items.map(r => (
        <div key={r.id} className="min-w-0 rounded-lg p-5 org-card flex flex-col" style={{ background: '#fbf8ef' }} data-testid={`past-item-${r.id}`}>
          <div className="flex items-center gap-2 text-[12px] text-[#0d2a20]/60 mb-3">
            <span className="px-2 py-1 rounded flex items-center gap-1" style={{ background: '#ece5d3' }}><Headphones size={12} /> Recording</span>
            <span className="inline-flex items-center gap-1"><Calendar size={12} /> {r.date}</span>
            {r.tag && <span>{r.tag}</span>}
          </div>
          <h3 className="text-[20px] font-medium mb-1 leading-tight">{r.title}</h3>
          {r.speaker_name && <div className="text-[13px] text-[#0d2a20]/70 mb-2">with {r.speaker_name}{r.speaker_title ? ` · ${r.speaker_title}` : ''}</div>}
          {r.description && <div className="text-[13px] text-[#0d2a20]/70 mb-4 line-clamp-3">{r.description}</div>}
          <div className="mt-auto">
            <AudioPlayer testId={`player-${r.id}`} getUrl={async () => (await memberApi.get(`/member/recordings/${r.id}/stream-url`)).data.url} />
          </div>
        </div>
      ))}
    </div>
  );
};

const UpcomingEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    memberApi.get('/member/events').then(({ data }) => {
      const t = todayStr();
      setEvents(data.filter(e => (e.date || '') >= t && !['draft', 'cancelled'].includes(e.status)));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  return (
    <>
      {loading ? (
        <div className="rounded-lg p-10 text-center text-[14px] text-[#0d2a20]/60" style={{ background: '#fbf8ef' }}>Loading…</div>
      ) : events.length === 0 ? (
        <div className="rounded-lg p-10 text-center text-[14px] text-[#0d2a20]/60" style={{ background: '#fbf8ef' }}>No events yet.</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {events.map(e => (
            <div key={e.id} className="rounded-lg p-5 org-card flex flex-col" style={{ background: '#fbf8ef' }}>
              <div className="flex items-center gap-2 text-[12px] text-[#0d2a20]/60 mb-3">
                <span className="px-2 py-1 rounded flex items-center gap-1" style={{ background: '#ece5d3' }}><Calendar size={12} /> {e.date}</span>
                <span>{e.tag}</span>
              </div>
              <h3 className="text-[20px] font-medium mb-2 leading-tight">{e.title}</h3>
              <div className="text-[13px] text-[#0d2a20]/70 mb-4 line-clamp-3">{e.description}</div>
              <div className="mt-auto flex items-center justify-between">
                <div className="text-[12px] text-[#0d2a20]/60">{e.registered ? <span className="text-[#154a1c] font-medium inline-flex items-center gap-1"><CheckCircle2 size={12} /> Reserved</span> : `${e.seats_available} seats`}</div>
                <Link to={`/member/events/${e.id}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-green text-[13px]">Learn more</Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export const EventDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [e, setE] = useState(null);
  const [reserving, setReserving] = useState(false);
  const [openShare, setOpenShare] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareNote, setShareNote] = useState('');
  const [sharing, setSharing] = useState(false);

  const load = async () => {
    try { const { data } = await memberApi.get(`/member/events/${id}`); setE(data); }
    catch { toast({ title: 'Event not found' }); nav('/member/events'); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const reserve = async () => {
    setReserving(true);
    try {
      const { data } = await memberApi.post(`/member/events/${id}/reserve`, {});
      if (data.waitlisted) toast({ title: 'Added to waiting list', description: 'We\'ll let you know if a seat opens up.' });
      else toast({ title: 'Seat reserved', description: 'A calendar invite is on its way.' });
      load();
    } catch { toast({ title: 'Could not reserve' }); }
    finally { setReserving(false); }
  };

  const share = async () => {
    if (!shareEmail) return;
    setSharing(true);
    try { await memberApi.post(`/member/events/${id}/share`, { email: shareEmail, note: shareNote }); toast({ title: 'Invitation sent', description: `They'll be prompted to apply to join Vemoye before reserving.` }); setOpenShare(false); setShareEmail(''); setShareNote(''); }
    catch { toast({ title: 'Could not send' }); }
    finally { setSharing(false); }
  };

  if (!e) return <MemberLayout><div className="rounded-lg p-8" style={{ background: '#fbf8ef' }}>Loading…</div></MemberLayout>;

  return (
    <MemberLayout>
      <Link to="/member/events" className="inline-flex items-center gap-1 text-[13px] link-underline mb-4"><ArrowLeft size={14} /> All events</Link>
      <div className="rounded-lg p-6 md:p-10" style={{ background: '#fbf8ef' }}>
        <div className="flex items-center gap-2 text-[12px] text-[#0d2a20]/60 mb-4">
          <span className="px-2 py-1 rounded flex items-center gap-1" style={{ background: '#ece5d3' }}><Calendar size={12} /> {e.date}</span>
          <span>{e.tag}</span>
          <span className="inline-flex items-center gap-1"><Clock size={12} /> {e.start_time}–{e.end_time} {e.timezone}</span>
          <span className="inline-flex items-center gap-1"><MapPin size={12} /> {e.location}</span>
        </div>
        <h1 className="hero-title text-[36px] md:text-[52px] leading-tight mb-4">{e.title}</h1>
        <p className="text-[15px] leading-relaxed text-[#0d2a20]/85 whitespace-pre-wrap mb-6 max-w-[820px]">{e.description}</p>

        {e.has_recording && (
          <div className="mb-6 max-w-[820px]" data-testid="event-recording">
            <div className="section-eyebrow text-[#0d2a20]/60 mb-3">Recording</div>
            <AudioPlayer testId="event-detail-player" getUrl={async () => (await memberApi.get(`/member/recordings/${id}/stream-url`)).data.url} />
          </div>
        )}

        {e.speakers?.length > 0 && (
          <div className="mb-6">
            <div className="section-eyebrow text-[#0d2a20]/60 mb-3">Speakers</div>
            <div className="grid md:grid-cols-2 gap-3">
              {e.speakers.map((s, i) => (
                <div key={i} className="rounded-md p-4 flex items-center gap-3 border" style={{ borderColor: 'rgba(13,42,32,0.08)', background: 'white' }}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: '#0f3d2e', color: '#f4efe4' }}><User size={16} /></div>
                  <div>
                    <div className="text-[14px] font-medium">{s.name}</div>
                    <div className="text-[12px] text-[#0d2a20]/70">{s.bio}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center gap-4 flex-wrap pt-4 border-t" style={{ borderColor: 'rgba(13,42,32,0.08)' }}>
          <div className="text-[13px] text-[#0d2a20]/70 inline-flex items-center gap-1"><Users size={14} /> {e.seats_available} of {e.capacity} seats available</div>
          <div className="ml-auto flex gap-2">
            <button onClick={() => setOpenShare(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-ghost text-[13px]"><Share2 size={14} /> Invite someone</button>
            {e.registered ? (
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-[13px]" style={{ background: '#d6ecd6', color: '#154a1c' }}><CheckCircle2 size={14} /> Seat reserved</span>
            ) : (
              <button onClick={reserve} disabled={reserving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md btn-coral text-[14px] disabled:opacity-60">{reserving ? 'Reserving…' : 'Reserve a Seat'}</button>
            )}
          </div>
        </div>
      </div>

      {openShare && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: 'rgba(13,42,32,0.55)' }}>
          <div className="w-full max-w-[520px] rounded-lg p-6" style={{ background: '#fbf8ef' }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="hero-title text-[22px]">Invite someone</h3>
              <button onClick={() => setOpenShare(false)}><X size={18} /></button>
            </div>
            <p className="text-[13px] text-[#0d2a20]/70 mb-4">They'll receive an email about this event and a prompt to apply to join Vemoye. Only approved members can reserve a seat.</p>
            <input value={shareEmail} onChange={e => setShareEmail(e.target.value)} type="email" placeholder="Their email" className="w-full px-3 py-2 rounded-md bg-white border mb-3" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
            <textarea value={shareNote} onChange={e => setShareNote(e.target.value)} rows={3} placeholder="A short note (optional)" className="w-full px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setOpenShare(false)} className="px-4 py-2 rounded-md btn-ghost text-[13px]">Cancel</button>
              <button onClick={share} disabled={sharing || !shareEmail} className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-coral text-[13px] disabled:opacity-60"><Send size={13} /> Send invitation</button>
            </div>
          </div>
        </div>
      )}
    </MemberLayout>
  );
};
